import { Card } from '@mui/material';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { AppointmentList } from '../appointments/AppointmentList';
import { ConditionsList } from '../conditions/ConditionsList';
import ExpandableCard from '../../../../components/common/ExpandableCard';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { EpisodeOfCareList } from '../episodeOfCare/EpisodeOfCareList';
import { DeviceRequestsList } from '../devices/DeviceRequestsList';
import ImmunizationList from 'src/careflow/care-coordination/patient/immunization/ImmunizationList';
import ObservationDetails from 'src/careflow/care-coordination/patient/observations/ObservationDetails';

type Props = {
  patient: WrappedPatient | null;
};

export default function PatientHealth({ patient }: Props) {
  const { i18n } = useLocales();

  return (
    <>
      <Card sx={{ py: 2, boxShadow: 'none' }}>
        {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.IMMUNIZATION.VIEW] }) && (
          <ExpandableCard
            title={i18n('patients.details.immunizations.title', 'crs')}
            sxProp={{ m: 1 }}
          >
            <ImmunizationList patient={patient}/>
          </ExpandableCard>
        )}
        {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.OBSERVATION.VIEW] }) && (
          <ExpandableCard
            title={i18n('patients.details.observations.title', 'crs')}
            sxProp={{ m: 1 }}
          >
            <ObservationDetails 
              patient={patient} 
              permissions={{
                isAllowedAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.OBSERVATION.ADD] }),
                isAllowedEdit: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.OBSERVATION.EDIT] })
              }}
            />
          </ExpandableCard>
        )}
        {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.APPOINTMENT.VIEW] }) && (
          <ExpandableCard
            title={i18n('patients.details.appointments.title', 'crs')}
            sxProp={{ m: 1 }}
          >
            <AppointmentList 
              patient={patient} 
              permissions={{
                isAllowedAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.APPOINTMENT.ADD] }),
                isAllowedEdit: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.APPOINTMENT.EDIT] })
              }}
            />
          </ExpandableCard>
        )}
        {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CONDITION.VIEW] }) && (
          <ExpandableCard
            title={i18n('patients.details.conditions.title', 'crs')}
            sxProp={{ m: 1 }}
          >
            <ConditionsList patient={patient} />
          </ExpandableCard>
        )}
        {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.EPISODE_OF_CARE.VIEW] }) && (
          <ExpandableCard
            title={i18n('patients.details.episodeOfCare.title', 'crs')}
            sxProp={{ m: 1 }}
          >
            <EpisodeOfCareList patient={patient} />
          </ExpandableCard>
        )}
        {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.DEVICE.VIEW] }) && (
          <ExpandableCard title={i18n('patients.details.device.title', 'crs')} sxProp={{ m: 1 }}>
            <DeviceRequestsList 
              useExternalQuery={true}
              patient={patient}
              permissions={{
                isAllowedAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.OBSERVATION.ADD] }),
                isAllowedEdit: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.OBSERVATION.EDIT] })
              }}
            />
          </ExpandableCard>
        )}
      </Card>
    </>
  );
}
